import { useTranslation } from "react-i18next";
import { useRunArtifactContent } from "~/api/queries";
import { formatBytes } from "~/features/automation/format";
import { Dialog } from "~/foundation/components/Dialog";
import { Spinner } from "~/foundation/components/Spinner";

export interface ArtifactViewerProps {
  runId: string;
  path: string | null;
  version: number | undefined;
  onClose: () => void;
}

/** Pretty-print JSON, leave everything else exactly as the executor wrote it. */
function render(content: string, mediaType: string | null): string {
  if (mediaType?.includes("json")) {
    try {
      return JSON.stringify(JSON.parse(content), null, 2);
    } catch {
      return content;
    }
  }
  return content;
}

/**
 * One artifact's content, fetched on open.
 *
 * `contentWithheld` is not an error and is not rendered as one: bigger artifacts are published
 * by reference, so the mirror holds the audit row (path, size, hash, who produced it) without
 * the bytes. Saying so is the honest answer; an empty pane would read as "the file is empty".
 */
export function ArtifactViewer({ runId, path, version, onClose }: ArtifactViewerProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useRunArtifactContent(runId, path, version);

  return (
    <Dialog
      open={path !== null}
      onOpenChange={(next) => !next && onClose()}
      title={path ?? t("Artifact")}
      closeLabel={t("Close")}
      size="full"
    >
      {isLoading ? <Spinner label={t("Loading")} /> : null}

      {isError ? (
        <p className="text-body text-danger">
          {error instanceof Error ? error.message : t("The artifact could not be read.")}
        </p>
      ) : null}

      {data ? (
        <div className="flex flex-col gap-2">
          <p className="text-small text-text-tertiary">
            {[
              t("version {{version}}", { version: data.version }),
              formatBytes(data.bytes),
              data.mediaType,
              data.producedBy ? t("produced by {{node}}", { node: data.producedBy }) : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>

          {data.contentWithheld || data.content === null ? (
            <p className="text-body text-text-secondary">
              {t(
                "The content is not on the server — bigger artifacts publish by reference only, so the mirror holds the audit row without the bytes.",
              )}
            </p>
          ) : (
            <pre
              dir="ltr"
              className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded-md border border-divider bg-bg-secondary p-3 font-mono text-small text-text"
            >
              {render(data.content, data.mediaType)}
            </pre>
          )}
        </div>
      ) : null}
    </Dialog>
  );
}
